import { ATLAS_BUCKET_VALUES, ICP_BUCKET_VALUES, PROSPECT_BUCKET_VALUES } from '@parcel/types';
import { z } from 'zod';
import { ACCOUNT_TYPE_VALUES } from './entities/account.js';
import { PRIMARY_USE_VALUES, PROJECT_STAGE_VALUES } from './entities/project.js';
import { describeWhere } from './where/describe.js';
import { safeParseWhere, whereScoreDatasets } from './where/schema.js';
import { type WhereGroup, isWhereGroup } from './where/types.js';

export const SCORE_ENTITIES = ['account', 'project', 'contact'] as const;
export type ScoreEntity = (typeof SCORE_ENTITIES)[number];

export const SCORE_TEMPLATE_KEYS = ['icp', 'atlas', 'prospect'] as const;
export type ScoreTemplateKey = (typeof SCORE_TEMPLATE_KEYS)[number];
export const TEMPLATE_KEY_BY_ENTITY: Record<ScoreEntity, ScoreTemplateKey> = {
  account: 'icp',
  project: 'atlas',
  contact: 'prospect',
};

/** Account types a workspace sells to; agencies, law firms and other carry no buyer personas. */
export const SEED_STAKEHOLDER_TYPES = ACCOUNT_TYPE_VALUES.filter(
  (t) => !['agency', 'law_firm', 'other'].includes(t),
) as [string, ...string[]];
/** The most focus markets one seed takes. */
export const SEED_MARKETS_MAX = 20;
/** What a seed's size floor measures: residential units, square feet or construction cost. */
export const SEED_SIZE_FLOOR_KINDS = ['units', 'sqft', 'cost'] as const;
export type SeedSizeFloorKind = (typeof SEED_SIZE_FLOOR_KINDS)[number];
/** The ids of the criteria that carry each answer, so the web can read the answers back. */
export const SEED_CRITERION_IDS = {
  market: 'market',
  sector: 'sector',
  stage: 'stage',
  size: 'size',
  stakeholder: 'stakeholder',
} as const;

const distinct = <T>(values: T[]) => [...new Set(values)];

/** The onboarding answers a seed takes; the route, the measurement script and the web check against it. */
export const seedAnswersSchema = z
  .object({
    markets: z
      .array(z.string().trim().min(1).max(100))
      .min(1)
      .max(SEED_MARKETS_MAX)
      .transform(distinct),
    sectors: z.array(z.enum(PRIMARY_USE_VALUES)).min(1).transform(distinct),
    stages: z.array(z.enum(PROJECT_STAGE_VALUES)).min(1).transform(distinct),
    stakeholders: z.array(z.enum(SEED_STAKEHOLDER_TYPES)).min(1).transform(distinct),
    size_floor: z
      .object({ kind: z.enum(SEED_SIZE_FLOOR_KINDS), min: z.number().int().positive() })
      .strict()
      .optional(),
  })
  .strict();
export type SeedAnswers = z.input<typeof seedAnswersSchema>;

export const SCORE_IMPACTS = [
  'very_important',
  'important',
  'somewhat_important',
  'not_important',
  'disqualify',
] as const;
export type ScoreImpact = (typeof SCORE_IMPACTS)[number];
export const IMPACT_WEIGHT: Record<ScoreImpact, number> = {
  very_important: 4,
  important: 3,
  somewhat_important: 2,
  not_important: 1,
  disqualify: 0,
};
export const IMPACT_LABEL: Record<ScoreImpact, string> = {
  very_important: 'Very important',
  important: 'Important',
  somewhat_important: 'Somewhat important',
  not_important: 'Not important',
  disqualify: 'Disqualify',
};

/** Top bucket at or above 75, then 50, then 25; below 25 is the bottom bucket. */
export const BUCKET_THRESHOLDS = [75, 50, 25] as const;
export function bucketIndexFor(score: number): 0 | 1 | 2 | 3 {
  if (score >= BUCKET_THRESHOLDS[0]) return 0;
  if (score >= BUCKET_THRESHOLDS[1]) return 1;
  if (score >= BUCKET_THRESHOLDS[2]) return 2;
  return 3;
}

/** Matched weight over total weight, 0-100; 0 when a disqualifier fired; null when nothing else carries weight. */
export function scoreOf(
  matchedWeight: number,
  totalWeight: number,
  disqualified: boolean,
): number | null {
  if (disqualified) return 0;
  if (totalWeight <= 0) return null;
  return Math.round((100 * matchedWeight) / totalWeight);
}

export interface ScoreBucket {
  key: string;
  label: string;
}

function buckets(keys: readonly string[], labels: readonly string[]): ScoreBucket[] {
  return keys.map((key, i) => ({ key, label: labels[i] as string }));
}

export const TEMPLATE_DEFAULTS: Record<
  ScoreTemplateKey,
  { entity: ScoreEntity; name: string; buckets: ScoreBucket[] }
> = {
  icp: {
    entity: 'account',
    name: 'ICP Score',
    buckets: buckets(ICP_BUCKET_VALUES, ['Tier 1', 'Tier 2', 'Tier 3', 'Disqualified']),
  },
  atlas: {
    entity: 'project',
    name: 'Atlas Score',
    buckets: buckets(ATLAS_BUCKET_VALUES, ['Priority', 'Secondary', 'Watch', 'Pass']),
  },
  prospect: {
    entity: 'contact',
    name: 'Prospect Score',
    buckets: buckets(PROSPECT_BUCKET_VALUES, ['Excellent', 'Good', 'Fair', 'Not a fit']),
  },
};

/** One criterion: a condition on the score's records, weighted by its impact. */
export interface ScoreCriterion {
  id: string;
  label?: string;
  impact: ScoreImpact;
  where: WhereGroup;
}

const CRITERION_ID = z.string().regex(/^[a-z0-9_]{1,40}$/, 'ids are 1-40 of a-z, 0-9 and _');
const CRITERION_LABEL = z.string().trim().min(1).max(80);

const hasEmptyGroup = (group: WhereGroup): boolean =>
  group.rules.some(
    (node) => isWhereGroup(node) && (node.rules.length === 0 || hasEmptyGroup(node)),
  );

/** One criterion for this dataset: `where` is checked against its field list and may read only an earlier score. */
export function scoreCriterionSchema(entity: ScoreEntity): z.ZodType<ScoreCriterion> {
  return z
    .object({
      id: CRITERION_ID,
      label: CRITERION_LABEL.optional(),
      impact: z.enum(SCORE_IMPACTS),
      where: z.unknown(),
    })
    .strict()
    .transform((c, ctx): ScoreCriterion => {
      const issue = (message: string) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['where'], message });
        return z.NEVER;
      };
      const parsed = safeParseWhere(entity, c.where);
      if (!parsed.ok) return issue(parsed.error.message);
      const where = parsed.where;
      if (where.rules.length === 0) return issue('A criterion needs at least one rule.');
      // An empty group compiles to TRUE, so it would match every record.
      if (hasEmptyGroup(where)) return issue('Each group needs at least one condition.');
      const reads = whereScoreDatasets(entity, where);
      if (reads.includes(entity)) return issue('A score cannot read its own score.');
      if (reads.some((d) => SCORE_ENTITIES.indexOf(d) > SCORE_ENTITIES.indexOf(entity))) {
        return issue('A score can read only an earlier score.');
      }
      return { id: c.id, ...(c.label ? { label: c.label } : {}), impact: c.impact, where };
    }) as unknown as z.ZodType<ScoreCriterion>;
}

/** The criterion's label, else its rules in words, cut to 80 characters. */
export function criterionLabel(entity: ScoreEntity, criterion: ScoreCriterion): string {
  if (criterion.label?.trim()) return criterion.label.trim();
  const text = describeWhere(entity, criterion.where);
  return text.length <= 80 ? text : `${text.slice(0, 79).trimEnd()}…`;
}

const LABEL = z.string().trim().min(1).max(30);

/** The most criteria one score holds; the settings page stops adding at it. */
export const SCORE_CRITERIA_MAX = 40;

export function scoreDefinitionInputSchema(entity: ScoreEntity) {
  return z
    .object({
      name: z.string().trim().min(1).max(60).optional(),
      /** The version the caller read; a save against a newer one answers version_conflict. */
      expected_version: z.number().int().positive().optional(),
      bucket_labels: z.tuple([LABEL, LABEL, LABEL, LABEL]),
      criteria: z.array(scoreCriterionSchema(entity)).max(SCORE_CRITERIA_MAX),
    })
    .strict()
    .superRefine((d, ctx) => {
      const ids = d.criteria.map((c) => c.id);
      if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'criteria ids must be unique',
          path: ['criteria'],
        });
      }
    });
}
export type ScoreDefinitionInput = z.infer<ReturnType<typeof scoreDefinitionInputSchema>>;
